import { BaseViewStore } from 'core/stores';
import { CancelForm } from 'modules/order/forms';
import { action, makeObservable } from 'mobx';
import FulfillmentOrderStore from './FulfillmentOrderStore';
import OrderStore from './OrderStore';
import { LookupStore } from 'modules/lookup/stores';
import {
	cancelReasons,
	fulfillmentOrderAction,
	fulfillmentOrderStatus,
	lookupRouteName,
	responseStatus,
	shipmentInfoStatusEnum,
	sourceOfChange,
} from 'common/constants';
import { isSuperAdminOrganization, notificationError, translate } from 'core/utils';
import { BaasicDropdownViewStore } from 'common/stores';
import _ from 'lodash';
import { CancelUncancelResult, ResultReport } from '../pages';
import { isSome } from 'core/utils';

class CancelViewStore extends BaseViewStore {
	activeShipment = null;
	selectedItem = null;
	selectedItems = null;
	debouncedCancelSearch = null;
	isBulkAction = false;

	form = new CancelForm({
		onSuccess: async () => {
			if (this.selectedItems == null) {
				await this.cancel();
			} else {
				await this.cancelBulk();
			}
		},
	});

	constructor(rootStore, { modalParams }) {
		super(rootStore);
		makeObservable(this);
		this.modalParams = modalParams;
		this.lookupStore = new LookupStore(rootStore);
		this.fulfillmentOrderStore = new FulfillmentOrderStore(rootStore);
		this.orderStore = new OrderStore(rootStore);
		this.isSuperAdminOrganization = isSuperAdminOrganization(rootStore);
		this.initialize(_.get(this.modalParams, 'data.selectedItemId'), _.get(this.modalParams, 'data.selectedItemIds'));
	}

	@action.bound
	initialize = async (selectedItemId, selectedItemIds) => {
		this.suspend();
		this.isMarketplaceOrder = this.modalParams.data.isMarketplaceOrder;
		if (selectedItemId != null) {
			if (this.isMarketplaceOrder) 
				await this.getMarketplaceOrder(selectedItemId);
			else 
				await this.getFulfillmentOrder(selectedItemId);
		} else {
			this.isBulkAction = true;
			if (!this.isMarketplaceOrder) {
				await this.findFulfillmentOrders(selectedItemIds);
			}
		}
		
		this.cancelReasonDropdownStore = new BaasicDropdownViewStore({
			store: new LookupStore(this.rootStore),
			field: this.form.$('cancelReasonId'),
			lookupRoute: lookupRouteName.cancelReason,
			filterCallback: options => {
				return options
					.filter(
						option =>
							option.abrv !== cancelReasons.canceledBySuperadmin &&
							option.abrv !== cancelReasons.warehouseChangedOnChannel
					)
					.map(option => {
						return this.cancelReasonDropdownStore.constructOption(option);
					});
			},
			defaultInitialValue: !this.isMarketplaceOrder
				? this.isSuperAdminOrganization && _.get(this, 'selectedItem.requestedCancelReasonId')
					? { path: 'value', value: this.selectedItem.requestedCancelReasonId }
					: null
				: null,
		});
		this.resume();
	};

	@action.bound
	cancel = async (voidOption) => {
		this.suspend();
		// Fulfillment order cancel
		if (!this.isMarketplaceOrder) {
			try {
				const { cancelReasonId } = this.form.values();
				const cancelParams = {
					fulfillmentOrderId: this.selectedItem.id,
					cancelReasonId: cancelReasonId,
					voidOption: voidOption,
				};
				const response = await this.fulfillmentOrderStore.cancelFulfillmentOrder(
					cancelParams,
					sourceOfChange.cancelFulfillmentOrder
				);
				if (response.status === responseStatus.error) {
					this.rootStore.notificationStore.error(response.result);
				} else if (response.status === responseStatus.success) {
					this.rootStore.notificationStore.success([
						'CANCELVIEW.SUCCESS',
						{ id: this.selectedItem.fulfillmentOrderId },
					]);
					if (voidOption !== 3 && this.modalParams.data.resetPrintingData) {
						this.modalParams.data.resetPrintingData();
					}
				} else {
					this.rootStore.notificationStore.warning(response.result);
				}
			} catch (err) {
				if (err.statusCode == 400 && _.get(err, 'data.message') == 'FULFILLMENTORDER.ALREADYPROCESSING') {
					this.rootStore.notificationStore.error([
						'FULFILLMENTORDER.ALREADYPROCESSING',
						{ id: this.selectedItem.fulfillmentOrderId },
					]);
				} else {
					notificationError({ store: this.rootStore.notificationStore, err });
				}
			}
		}
		// Marketplace order cancel
		else {
			try {
				const { cancelReasonId } = this.form.values();
				const cancelParams = {
					orderId: this.selectedItem.id,
					cancelReasonId: cancelReasonId,
				};
				const response = await this.orderStore.cancelMarketplaceOrder(
					cancelParams,
					sourceOfChange.cancelMarketplaceOrder
				);
				if (response.result == 'success') {
					this.rootStore.notificationStore.success('Order cancel success. Click here for more details', false, {
						autoClose: true,
						onClick: () => {
							this.openModal({
								data: response,
								component: CancelUncancelResult,
							});
						},
					});
				} else if (response.result == 'partial') {
					this.rootStore.notificationStore.warning('Order partially canceled. Click here for more details.', false, {
						autoClose: false,
						onClick: () => {
							this.openModal({
								data: response,
								component: CancelUncancelResult,
							});
						},
					});
				} else {
					this.rootStore.notificationStore.error('Order cancel failed. Click here for more details.', false, {
						autoClose: false,
						onClick: () => {
							this.openModal({
								data: response,
								component: CancelUncancelResult,
							});
						},
					});
				}
			} catch (err) {
				notificationError({ store: this.rootStore.notificationStore, err });
			}
		}
		
		this.modalParams.success();
		this.resume();
	};

	@action.bound
	cancelBulk = async voidOption => {
		this.suspend();
		try {
			const { cancelReasonId } = this.form.values();
			const cancelBulkParams = {
				fulfillmentOrderIds: this.selectedItems.map(item => item.id),
				cancelReasonId: cancelReasonId,
				voidOption: voidOption,
			};
			const notificationId = this.rootStore.notificationStore.load(translate('BULKNOTIFICATION.CANCEL'));
			const response = await this.fulfillmentOrderStore.cancelFulfillmentOrdersBulk(
				cancelBulkParams,
				sourceOfChange.cancelFulfillmentOrdersBulk
			);
			this.rootStore.notificationStore.dismiss(notificationId);

			let notificationParams = {
				autoClose: response.every(item => item.status === responseStatus.success),
				onClick: () => {
					this.openModal({
						data: response,
						component: ResultReport,
						fulfillmentOrderAction: fulfillmentOrderAction.BulkCancel,
					});
				},
			};

			if (response.every(item => item.status === responseStatus.error)) {
				this.rootStore.notificationStore.error('CANCELVIEW.ERRORBULK', false, notificationParams);
			} else if (response.every(item => item.status === responseStatus.success)) {
				this.rootStore.notificationStore.success('CANCELVIEW.SUCCESSBULK');
			} else {
				this.rootStore.notificationStore.warning('CANCELVIEW.WARNINGBULK', notificationParams);
			}
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.modalParams.success();
		this.resume();
	};

	@action.bound
	findFulfillmentOrders = async selectedItemIds => {
		this.selectedItems = (await this.fulfillmentOrderStore.findFulfillmentOrder({
			page: 1,
			rpp: 100,
			ids: selectedItemIds,
			embed: ['fulfillmentOrderStatus', 'onHoldReason', 'shipmentInfos'],
		})).item;
	};

	@action.bound
	getFulfillmentOrder = async selectedItemId => {
		this.selectedItem = await this.fulfillmentOrderStore.getFulfillmentOrder(selectedItemId, {
			embed: ['fulfillmentOrderStatus', 'onHoldReason', 'shipmentInfos'],
		});
		if (
			this.isSuperAdminOrganization ||
			[fulfillmentOrderStatus.entered, fulfillmentOrderStatus.assigned].includes(
				this.selectedItem.fulfillmentOrderStatus.abrv
			)
		)
			this.activeShipment = _.find(this.selectedItem.shipmentInfos, item => {
				return item.status == shipmentInfoStatusEnum.active;
			});
	};

	@action.bound
	getMarketplaceOrder = async selectedItemId => {
		this.selectedItem = await this.orderStore.getOrder(selectedItemId, {
			embed: ['orderStatus'],
		});
	}

	getVoidLabelConfirmModalProps = () => {
		return {
			onConfirm: async () => {
				if (this.selectedItems == null) await this.cancel(1);
				else await this.cancelBulk(1);
			},
			onCancel: async () => {
				if (this.selectedItems == null) await this.cancel(0);
				else await this.cancelBulk(0);
			},
		};
	};

	@action.bound
	openVoidActiveLabelConfirmModal() {
		if (this.isBulkAction) {
			for (const selectedItem of this.selectedItems) {
				if (
					!this.isSuperAdminOrganization &&
					![fulfillmentOrderStatus.entered, fulfillmentOrderStatus.assigned].includes(
						selectedItem.fulfillmentOrderStatus.abrv
					)
				) {
					continue;
				}
				const activeShipment = _.find(selectedItem.shipmentInfos, item => {
					return item.status == shipmentInfoStatusEnum.active;
				});
				if (activeShipment != null) {
					var hasActiveShipments = true;
					var hasActiveShipmentsWithBuyShipping = activeShipment.isBuyShipping == true;
					if (hasActiveShipmentsWithBuyShipping == true) break;
				}
			}
		}

		if (this.activeShipment != null || hasActiveShipments) {
			const message =
				hasActiveShipmentsWithBuyShipping || (!this.isBulkAction && _.get(this.activeShipment, 'isBuyShipping'))
					? this.isBulkAction
						? 'CANCELVIEW.BULKVOIDLABEL'
						: ['CANCELVIEW.VOIDLABEL', { fulfillmentOrderId: this.selectedItem.fulfillmentOrderId }]
					: null;
			if (message == null) {
				this.isBulkAction ? this.cancelBulk(0) : this.cancel(0);
			} else {
				this.openConfirmationModal({ ...this.getVoidLabelConfirmModalProps(), message });
			}
		} else {
			if (this.isBulkAction) {
				this.cancelBulk(3);
			} else {
				this.cancel(3);
			}
		}
	}
}

export default CancelViewStore;
